/* ==========================================================================
   Sticky header — U&S Clothing
   assets/sticky-header.js

   Replaces the theme's own sticky-header.js.

   WHY IT WAS REPLACED

   The original made the header stick by taking it out of the document:

       .shopify-section-header-sticky header { position: fixed; top: 0; }
       sticky-header { min-height: var(--sticky-header-height-desktop); }

   Once the inner <header> is fixed it occupies no space, so the wrapper has
   to hold its place — and it does that with a height the script measures and
   writes into a CSS variable.

   Every gap reported on this site traced back to that one measurement:

     - measured while a mega menu was open, so it came out far too tall
     - measured before the fonts or the logo had loaded, so too short
     - written once and then stale, with nothing to correct it
     - the sticky class left on at the top of the page after a fast scroll,
       so the placeholder stayed while the header was collapsed

   Each of those was patched in turn and another took its place, because the
   measurement itself is the fragile part.

   WHAT THIS DOES INSTEAD

   position: sticky. The header stays in the flow of the page, so it keeps
   its own space — no placeholder, no measurement, no variable, and no way
   for any of them to be wrong. The browser handles it.

   The behaviour the client sees is unchanged: the header stays at the top,
   hides when scrolling down, and comes back when scrolling up.

   --header-height is still published, because the faceted filters and the
   product tabs position themselves against it. It is read from the element
   rather than computed, and only used for offsets — never to hold a space
   open, so a wrong value cannot leave a gap.
   ========================================================================== */
(function () {
  'use strict';

  /* How far down the page before hiding on a downward scroll. Below this the
     header always shows, so a small scroll near the top never flickers. */
  var HIDE_AFTER = 180;

  /* Ignore anything smaller than this. Momentum scrolling on a phone sends a
     stream of tiny deltas and without it the header would flutter. */
  var MIN_DELTA = 6;

  if (window.customElements && !customElements.get('sticky-header')) {
    customElements.define('sticky-header', class StickyHeader extends HTMLElement {

      connectedCallback() {
        this.header = document.querySelector('.section-header');
        this.inner  = this.querySelector('header');
        if (!this.header || !this.inner) return;

        this.lastY = window.scrollY || 0;
        this.hidden = false;

        this.classList.add('cw-sticky');
        this.header.classList.add('cw-sticky-host');

        /* The theme collapses the navigation into a button once stuck. Keep
           that, since the CSS for it is already written. */
        if (this.dataset.stickyType === 'toggle-nav') {
          this.header.classList.add('toggle-nav');
        }

        this.onScroll = this.onScroll.bind(this);
        this.publishHeight = this.publishHeight.bind(this);

        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.publishHeight);

        /* An open menu must never be left hidden above the fold. */
        this.addEventListener('focusin', () => this.show());
        document.addEventListener('shopify:section:load', this.publishHeight);

        this.publishHeight();
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(this.publishHeight);
        }
        window.addEventListener('load', this.publishHeight);
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.publishHeight);
      }

      /* Published for the filters and the tabs, which sit below the header
         and need to know how far down to start. Nothing holds a space open
         with it, so a stale value cannot produce a gap. */
      publishHeight() {
        var h = Math.round(this.inner.getBoundingClientRect().height);
        if (!h || h === this.published) return;
        this.published = h;
        document.documentElement.style.setProperty('--header-height', h + 'px');
      }

      /* A menu open means the shopper is using the header — never move it. */
      menuIsOpen() {
        return !!(
          this.querySelector('details[open]') ||
          document.querySelector('.menu-drawer[open], .header__search--open') ||
          document.body.classList.contains('overflow-hidden-tablet')
        );
      }

      onScroll() {
        var y = window.scrollY || document.documentElement.scrollTop || 0;
        var delta = y - this.lastY;

        if (Math.abs(delta) < MIN_DELTA) return;
        this.lastY = y;

        if (this.menuIsOpen()) { this.show(); return; }

        if (y <= HIDE_AFTER) { this.show(); return; }

        if (delta > 0) this.hide();
        else this.show();
      }

      /* Moved with a transform, so the element keeps its space in the page
         whether it is visible or not. That is the whole point: there is
         never a hole for it to leave behind. */
      hide() {
        if (this.hidden) return;
        this.hidden = true;
        this.header.classList.add('cw-sticky-hidden');
      }

      show() {
        if (!this.hidden) return;
        this.hidden = false;
        this.header.classList.remove('cw-sticky-hidden');
      }
    });
  }
})();